import React from 'react';
import { useTranslation } from 'react-i18next';
import { Headphones, Plus, TrendingUp, Receipt, Calendar } from 'lucide-react';
import { StepHeader, WizField, WizTextarea } from '../wizardUi';

function AgentOption({ selected, icon, title, hint, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`flex items-center gap-4 rounded-[22px] border-[1.5px] bg-white/[0.04] p-4 text-left transition-all lg:flex-1 ${
        selected
          ? 'border-primary shadow-[0_0_28px_rgba(124,58,237,0.28)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/20 text-[#b79cff]">
        {icon}
      </div>
      <div>
        <div className="text-[15px] font-semibold">{title}</div>
        <div className="mt-0.5 text-[12.5px] text-white/50">{hint}</div>
      </div>
    </button>
  );
}

function Chip({ icon, children }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.05] py-1.5 pl-3 pr-3.5 text-[12px] font-medium text-white/80 lg:hidden">
      <span className="text-[#c9bdf0]">{icon}</span>
      {children}
    </span>
  );
}

function CapabilityCard({ icon, title, desc }) {
  return (
    <div className="hidden flex-col gap-3 rounded-[22px] border border-white/[0.08] bg-white/[0.04] p-5 lg:flex">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/20 text-[#b79cff]">{icon}</div>
      <b className="text-[14.5px] font-semibold">{title}</b>
      <span className="text-[13px] leading-relaxed text-white/50">{desc}</span>
    </div>
  );
}

export default function Step1AgentType({ wizard }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-5 lg:gap-6">
      <StepHeader eyebrow={t('wizardIg.eyebrow', { n: 1 })} title={t('wizardIg.title0')} />

      <div className="flex flex-col gap-3 lg:flex-row">
        <AgentOption
          selected={wizard.agentType === 'builtin'}
          onClick={() => wizard.setAgentType('builtin')}
          icon={<Headphones size={21} />}
          title={t('wizardIg.optBuiltinTitle')}
          hint={t('wizardIg.optBuiltinHint')}
        />
        <AgentOption
          selected={wizard.agentType === 'other'}
          onClick={() => wizard.setAgentType('other')}
          icon={<Plus size={21} />}
          title={t('wizardIg.optOtherTitle')}
          hint={t('wizardIg.optOtherHint')}
        />
      </div>

      {wizard.agentType === 'builtin' ? (
        <div className="flex flex-col gap-4">
          <p className="max-w-[60ch] text-[14px] leading-relaxed text-white/55 lg:text-[15px]">{t('wizardIg.builtinDesc')}</p>
          <div className="flex flex-wrap gap-2 lg:grid lg:grid-cols-3 lg:gap-4">
            <Chip icon={<TrendingUp size={14} />}>{t('wizardIg.chipEscalate')}</Chip>
            <Chip icon={<Receipt size={14} />}>{t('wizardIg.chipOrders')}</Chip>
            <Chip icon={<Calendar size={14} />}>{t('wizardIg.chipReservations')}</Chip>
            <CapabilityCard icon={<TrendingUp size={18} />} title={t('wizardIg.dcardEscalateTitle')} desc={t('wizardIg.dcardEscalateDesc')} />
            <CapabilityCard icon={<Receipt size={18} />} title={t('wizardIg.dcardOrdersTitle')} desc={t('wizardIg.dcardOrdersDesc')} />
            <CapabilityCard icon={<Calendar size={18} />} title={t('wizardIg.dcardReservationsTitle')} desc={t('wizardIg.dcardReservationsDesc')} />
          </div>
        </div>
      ) : (
        <WizField label={t('wizardIg.otherLabel')}>
          <WizTextarea
            rows={4}
            value={wizard.agentOtherDescription}
            onChange={(e) => wizard.setAgentOtherDescription(e.target.value)}
            placeholder={t('wizardIg.otherPlaceholder')}
          />
        </WizField>
      )}
    </div>
  );
}
